import { format } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Mail,
  Calendar,
  Clock,
  CreditCard,
  Shield,
  CheckCircle,
  AlertCircle,
  Loader2,
  Trash2,
  UserX,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface AuthUserInfo {
  confirmed_at?: string;
  email_confirmed_at?: string;
  created_at: string;
  last_sign_in_at?: string;
  phone?: string;
  phone_confirmed_at?: string;
  is_anonymous?: boolean;
  app_metadata: Record<string, unknown> & {
    provider?: string;
    providers?: string[];
  };
  user_metadata: Record<string, unknown>;
  aud?: string;
  role?: string;
  updated_at?: string;
  identities?: {
    provider: string;
    id: string;
    created_at: string;
    updated_at: string;
  }[];
}

interface User {
  id: number;
  name: string;
  email: string;
  avatarUrl: string;
  isActive: boolean;
  ageGroup?: string;
  registeredAt: string;
  roles?: Array<{
    id: number;
    name: string;
    isSystem: boolean;
  }>;
}

interface UserSubscription {
  id: number;
  status: string;
  price: number;
  currencyCode: string;
  subscriptionStart: string;
  subscriptionEnd?: string;
  autoRenew: boolean;
  paymentMethodId?: number;
  subscriptionPlan?: {
    name: string;
    duration: number;
    durationType: string;
  };
}

interface AccountDeletionFeedback {
  id: number;
  user_id: number | null;
  auth_uid: string | null;
  email: string;
  name: string | null;
  reason: string;
  additional_feedback: string | null;
  deletion_requested_at: string;
  user_agent: string | null;
  ip_address: string | null;
  app_version: string | null;
  platform: string | null;
  subscription_status: string | null;
  last_login_at: string | null;
  account_age_days: number | null;
  total_books_read: number | null;
  total_reading_time_minutes: number | null;
  created_at: string;
  updated_at: string;
}

interface UserProfileHeaderProps {
  user: User;
  authUserInfo?: AuthUserInfo | null;
  currentSubscription?: UserSubscription | null;
  latestSubscription?: UserSubscription | null;
  isLoadingSubscription: boolean;
  onSwitchToTab: (tab: string) => void;
  deletionFeedback?: AccountDeletionFeedback | null;
  isLoadingDeletionFeedback?: boolean;
  isBetaTester?: boolean;
}

// Get color for role badge
const getRoleBadgeColor = (roleId: number): string => {
  // Special colors for specific roles
  if (roleId === 1) {
    // SuperAdmin - Red
    return "bg-red-500/10 text-red-500";
  }
  if (roleId === 5) {
    // Standard User - Blue
    return "bg-blue-500/10 text-blue-500";
  }

  const colorPairs = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-500",
    "bg-amber-500/10 text-amber-500",
    "bg-green-500/10 text-green-500",
    "bg-purple-500/10 text-purple-500",
    "bg-rose-500/10 text-rose-500",
    "bg-teal-500/10 text-teal-500",
    "bg-orange-500/10 text-orange-500",
    "bg-indigo-500/10 text-indigo-500",
  ];

  return colorPairs[roleId % colorPairs.length];
};

const getSubscriptionStatusColor = (isActive: boolean) => {
  return isActive
    ? "bg-green-500 hover:bg-green-600"
    : "bg-gray-500 hover:bg-gray-600";
};

const formatAuthDate = (dateString?: string) => {
  if (!dateString) return "Never";
  return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a");
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "bg-green-500";
    case "cancelled":
      return "bg-yellow-500";
    case "expired":
      return "bg-gray-500";
    default:
      return "bg-blue-500";
  }
};

const formatStatusText = (status: string) => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const calculateProgress = (start: string, end: string) => {
  const now = new Date().getTime();
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();

  return Math.min(
    100,
    Math.max(0, Math.floor(((now - startTime) / (endTime - startTime)) * 100))
  );
};

// Determine effective status based on end date
const getEffectiveStatus = (
  status?: string,
  subscriptionEnd?: string | null
): "active" | "cancelled" | "expired" | "pending" | "unknown" => {
  // If explicitly cancelled, expired, or pending, preserve that status
  if (status === "cancelled" || status === "expired" || status === "pending") {
    return status;
  }

  // Only override "active" status if end date has passed
  if (status === "active" && subscriptionEnd) {
    const end = new Date(subscriptionEnd);
    if (!isNaN(end.getTime()) && end < new Date()) {
      return "expired";
    }
  }

  // Return the original status or default to unknown
  if (status === "active") {
    return status;
  }
  return "unknown";
};

const UserProfileHeader: React.FC<UserProfileHeaderProps> = ({
  user,
  authUserInfo,
  currentSubscription,
  latestSubscription,
  isLoadingSubscription,
  onSwitchToTab,
  deletionFeedback,
  isLoadingDeletionFeedback,
  isBetaTester = false,
}) => {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left side - User Profile */}
          <div className="flex flex-col md:flex-row gap-6 flex-1 min-w-0">
            <Avatar className="w-24 h-24 flex-shrink-0">
              <AvatarImage src={user.avatarUrl} />
              <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="space-y-2 flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold truncate">{user.name}</h2>
                {user.name === "New User" &&
                authUserInfo &&
                !authUserInfo.email_confirmed_at ? (
                  <Badge className="bg-amber-500 hover:bg-amber-600">
                    Pending
                  </Badge>
                ) : user.isActive ? (
                  <Badge className="bg-green-500 hover:bg-green-600">
                    Active
                  </Badge>
                ) : (
                  <div className="flex items-center gap-2">
                    {isLoadingDeletionFeedback ? (
                      <Badge className="bg-gray-500 hover:bg-gray-600">
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Checking...
                      </Badge>
                    ) : deletionFeedback ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge className="bg-red-500 hover:bg-red-600">
                              <Trash2 className="h-3 w-3 mr-1" />
                              Deleted
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm">
                            <div className="space-y-1 text-sm">
                              <p>
                                <strong>Reason:</strong>{" "}
                                {deletionFeedback.reason}
                              </p>
                              <p>
                                <strong>Deleted:</strong>{" "}
                                {format(
                                  new Date(
                                    deletionFeedback.deletion_requested_at
                                  ),
                                  "MMM dd, yyyy 'at' h:mm a"
                                )}
                              </p>
                              {deletionFeedback.additional_feedback && (
                                <p>
                                  <strong>Notes:</strong>{" "}
                                  {deletionFeedback.additional_feedback}
                                </p>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge className="bg-orange-500 hover:bg-orange-600">
                              <UserX className="h-3 w-3 mr-1" />
                              Banned
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            User account has been banned by an administrator
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="truncate" title={user.email}>
                  {user.email}
                </span>
                {authUserInfo && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        {authUserInfo.email_confirmed_at ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        {authUserInfo.email_confirmed_at
                          ? "Email Verified"
                          : "Email Not Verified"}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {user.ageGroup && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="text-sm">Age Group:</span>
                  <Badge
                    variant="outline"
                    className="bg-blue-500/10 text-blue-500 border-blue-500/20"
                  >
                    {user.ageGroup}
                  </Badge>
                </div>
              )}

              {user.roles && user.roles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {user.roles.map((role) => (
                    <Badge
                      key={role.id}
                      className={cn(getRoleBadgeColor(role.id))}
                    >
                      {role.isSystem && <Shield className="h-3 w-3 mr-1" />}
                      {role.name}
                    </Badge>
                  ))}
                  {isBetaTester && (
                    <Badge
                      variant="outline"
                      className="bg-purple-500/10 text-purple-500 border-purple-500/20"
                    >
                      Beta Tester
                    </Badge>
                  )}
                  {(() => {
                    const s = currentSubscription || latestSubscription;
                    const effectiveStatus = getEffectiveStatus(
                      s?.status,
                      s?.subscriptionEnd
                    );
                    if (effectiveStatus === "active") {
                      return (
                        <Badge
                          variant="outline"
                          className="bg-amber-500/10 text-amber-600 border-amber-500/20"
                        >
                          Premium
                        </Badge>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                <Calendar className="w-4 h-4" />
                <span>
                  Joined {format(new Date(user.registeredAt), "MMM dd, yyyy")}
                </span>
              </div>
              {authUserInfo?.last_sign_in_at && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>
                    Last sign in {formatAuthDate(authUserInfo.last_sign_in_at)}
                  </span>
                </div>
              )}

              {authUserInfo && authUserInfo.phone && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge
                    variant="outline"
                    className={
                      authUserInfo.phone_confirmed_at
                        ? "bg-green-500/10 text-green-500 border-green-500/20"
                        : "bg-amber-500/10 text-amber-500 border-amber-500/20"
                    }
                  >
                    {authUserInfo.phone_confirmed_at
                      ? "Phone Verified"
                      : "Phone Not Verified"}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Right side - Premium Subscription Summary */}
          <div className="lg:w-72 lg:flex-shrink-0">
            <div
              className={`rounded-xl p-3 h-fit border transition-all duration-300 ${
                (() => {
                  const s = currentSubscription || latestSubscription;
                  return (
                    getEffectiveStatus(s?.status, s?.subscriptionEnd) ===
                    "active"
                  );
                })()
                  ? "bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-950/20 dark:via-yellow-950/20 dark:to-orange-950/20 border-amber-200/50 dark:border-amber-800/50 shadow-md"
                  : user.roles?.some((role) => role.name === "Super Admin")
                  ? "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950/20 dark:via-indigo-950/20 dark:to-purple-950/20 border-blue-200/50 dark:border-blue-800/50 shadow-md"
                  : "bg-muted/30 border-border/50"
              }`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                {user.roles?.some((role) => role.name === "Super Admin") ? (
                  <Shield className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                ) : (
                  <CreditCard className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                )}
                <span
                  className={`text-xs font-semibold ${
                    (() => {
                      const s = currentSubscription || latestSubscription;
                      return (
                        getEffectiveStatus(s?.status, s?.subscriptionEnd) ===
                        "active"
                      );
                    })()
                      ? "text-amber-700 dark:text-amber-300"
                      : user.roles?.some((role) => role.name === "Super Admin")
                      ? "text-blue-700 dark:text-blue-300"
                      : "text-muted-foreground"
                  }`}
                >
                  {user.roles?.some((role) => role.name === "Super Admin")
                    ? "Super Admin"
                    : (currentSubscription || latestSubscription)
                        ?.subscriptionPlan?.name || "Premium"}
                </span>
              </div>

              {isLoadingSubscription ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600 dark:border-amber-400"></div>
                </div>
              ) : currentSubscription || latestSubscription ? (
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm font-bold text-amber-800 dark:text-amber-200">
                        ₹
                        {(
                          currentSubscription || latestSubscription
                        )?.price.toFixed(2)}
                        <span className="text-xs text-amber-600 dark:text-amber-400 ml-1">
                          {
                            (currentSubscription || latestSubscription)
                              ?.currencyCode
                          }
                        </span>
                      </div>
                    </div>
                    {(() => {
                      const s = currentSubscription || latestSubscription;
                      const effectiveStatus = getEffectiveStatus(
                        s?.status,
                        s?.subscriptionEnd
                      );
                      return (
                        <Badge
                          className={`${getStatusColor(
                            effectiveStatus
                          )} text-xs px-2 py-0.5 font-semibold shadow-sm`}
                        >
                          {formatStatusText(effectiveStatus)}
                        </Badge>
                      );
                    })()}
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 text-xs">
                    <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-lg p-1.5 border border-amber-100 dark:border-amber-800">
                      <div className="text-amber-600 dark:text-amber-400 text-xs font-medium">
                        Start
                      </div>
                      <div className="font-semibold text-xs text-amber-900 dark:text-amber-100">
                        {format(
                          new Date(
                            (currentSubscription || latestSubscription)
                              ?.subscriptionStart || new Date()
                          ),
                          "MMM dd"
                        )}
                      </div>
                    </div>
                    <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-lg p-1.5 border border-amber-100 dark:border-amber-800">
                      <div className="text-amber-600 dark:text-amber-400 text-xs font-medium">
                        {(currentSubscription || latestSubscription)
                          ?.subscriptionEnd
                          ? "End"
                          : "Duration"}
                      </div>
                      <div className="font-semibold text-xs text-amber-900 dark:text-amber-100">
                        {(currentSubscription || latestSubscription)
                          ?.subscriptionEnd
                          ? format(
                              new Date(
                                (
                                  currentSubscription || latestSubscription
                                ).subscriptionEnd
                              ),
                              "MMM dd"
                            )
                          : "Lifetime"}
                      </div>
                    </div>
                  </div>

                  {(currentSubscription || latestSubscription)
                    ?.subscriptionEnd && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-amber-600 dark:text-amber-400 font-medium">
                          Progress
                        </span>
                        <span className="text-amber-700 dark:text-amber-300 font-semibold">
                          {calculateProgress(
                            (currentSubscription || latestSubscription)
                              .subscriptionStart,
                            (currentSubscription || latestSubscription)
                              .subscriptionEnd
                          )}
                          %
                        </span>
                      </div>
                      <div className="w-full bg-amber-100 dark:bg-amber-900/50 rounded-full h-1.5 shadow-inner">
                        <div
                          className="bg-gradient-to-r from-amber-400 to-orange-500 dark:from-amber-500 dark:to-orange-600 h-1.5 rounded-full transition-all duration-500 shadow-sm"
                          style={{
                            width: `${calculateProgress(
                              (currentSubscription || latestSubscription)
                                .subscriptionStart,
                              (currentSubscription || latestSubscription)
                                .subscriptionEnd
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : user.roles &&
                user.roles.some((role) => role.name === "Super Admin") ? (
                <div className="text-center py-2">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <div className="p-1.5 bg-blue-100 dark:bg-blue-900/50 rounded-full">
                      <Shield className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="font-semibold text-xs text-blue-900 dark:text-blue-100">
                      System Access
                    </span>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                    Unlimited privileges
                  </p>
                </div>
              ) : (
                <div className="text-center py-3">
                  <p className="text-xs text-muted-foreground mb-2">
                    No active subscription
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserProfileHeader;
